import React, { useMemo, useCallback, useRef, forwardRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import './CommonAgGrid.css';
import { ModuleRegistry } from 'ag-grid-enterprise';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import { FullScreenTableWrapper } from './FullScreenTableWrapper/FullScreenTableWrapper';
ModuleRegistry.registerModules([AllEnterpriseModule]);

const CommonAgGrid = forwardRef(({
  gridType = 'display',
  displayType = 'selectable',
  autoSizeColumns = false,
  rowData,
  columnDefs,
  defaultColDef: userDefaultColDef,
  onGridReady: userOnGridReady,
  ...restProps
}, ref) => {

  const focusedCellRef = useRef({ rowIndex: 0, colId: null });
  const fullscreenToggleRef = useRef(null);

  const handleCellFocused = useCallback((event) => {
    if (event.rowIndex != null && event.column != null) {
      focusedCellRef.current = {
        rowIndex: event.rowIndex,
        colId: event.column.getColId(),
      };
    }
  }, []);

  const baseDefaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    wrapText: true,
    autoHeaderHeight: true,
    headerCheckboxSelectionFilteredOnly: true,
    suppressMovable: true,
    menuTabs: ['generalMenuTab', 'filterMenuTab', 'columnsMenuTab'],
    suppressMenu: false,
  }), []);

  const excelDefaultColDef = useMemo(() => {
    if (gridType !== 'excel') return {};
    return {
      suppressKeyboardEvent: () => true,
      editable: (params) => {
        if (typeof params.colDef.editable === 'function') {
          return params.colDef.editable(params);
        }
        return !!params.colDef.editable;
      },
    };
  }, [gridType]);

  const defaultColDef = useMemo(() => ({
    ...baseDefaultColDef,
    ...excelDefaultColDef,
    ...userDefaultColDef,
  }), [baseDefaultColDef, excelDefaultColDef, userDefaultColDef]);

  const handleGridReady = useCallback((params) => {
    if (autoSizeColumns) {
      params.api.autoSizeAllColumns();
    }
    if (userOnGridReady) {
      userOnGridReady(params);
    }
  }, [autoSizeColumns, userOnGridReady]);

  const getMainMenuItems = useCallback((params) => {
    return [
      'pinSubMenu',
      'separator',
      'sortAscending',
      'sortDescending',
      'separator',
      'autoSizeThis',
      'autoSizeAll',
      'separator',
      'resetColumns',
      'separator',
      {
        name: 'Toggle Fullscreen',
        action: () => {
          if (fullscreenToggleRef.current) {
            fullscreenToggleRef.current();
          }
        },
        icon: '<span class="ag-icon ag-icon-maximize"></span>',
      },
    ];
  }, []);

  const gridOptions = useMemo(() => {
    const options = {
      domLayout: 'normal',
      suppressColumnVirtualisation: true,
      getMainMenuItems: getMainMenuItems,
    };

    const moveFocus = (api, targetRow, targetCol) => {
      if (!targetCol || targetRow == null || targetRow < 0) return;

      const colId = targetCol.getColId();
      api.setFocusedCell(targetRow, colId);
      api.clearRangeSelection?.();

      api.addCellRange({
        rowStartIndex: targetRow,
        rowEndIndex: targetRow,
        columns: [colId],
      });

      const editingDisabled = ["Trader"];
      if (!editingDisabled.includes(colId)) {
        setTimeout(() => {
          api.startEditingCell({
            rowIndex: targetRow,
            colKey: colId,
          });
        }, 50);
      }

      focusedCellRef.current = {
        rowIndex: targetRow,
        colId,
      };
    };

    const handleCellKeyDownExcel = (event) => {
      const { key, shiftKey } = event.event;
      const api = event.api;

      const allColumns = api.getAllDisplayedColumns();
      const { rowIndex, colId } = focusedCellRef.current;
      const currentCol = allColumns.find((col) => col.getColId() === colId);
      const currentIndex = allColumns.findIndex(
        (col) => col.getColId() === colId
      );

      if ((key === "ArrowRight" || key === "ArrowLeft") && !shiftKey) {
        event.event.preventDefault();
        api.stopEditing();

        const step = key === "ArrowRight" ? 1 : -1;
        let nextIndex = currentIndex + step;

        if (nextIndex < 0) nextIndex = allColumns.length - 1;
        else if (nextIndex >= allColumns.length) nextIndex = 0;

        const nextCol = allColumns[nextIndex];
        if (nextCol && rowIndex >= 0) {
          setTimeout(() => moveFocus(api, rowIndex, nextCol), 0);
        }
      }

      if (key === "ArrowDown" || key === "ArrowUp") {
        event.event.preventDefault();
        api.stopEditing();

        const step = key === "ArrowDown" ? 1 : -1;
        const nextRow = rowIndex + step;

        if (nextRow >= 0 && nextRow < api.getDisplayedRowCount()) {
          setTimeout(() => moveFocus(api, nextRow, currentCol), 0);
        }
      }
    };

    if (gridType === 'excel') {
      options.onCellKeyDown = handleCellKeyDownExcel;
      options.onCellFocused = handleCellFocused;
      options.enableRangeSelection = true;
      options.enableCellTextSelection = false;
      options.enableFillHandle = true;
      options.clipboardDelimiter = '\t';
      options.suppressRowClickSelection = true;
      options.stopEditingWhenCellsLoseFocus = true;

      options.suppressCopyRowsToClipboard = true;
      options.suppressClipboardPaste = false;

      options.processCellForClipboard = (params) => params.value;
      options.processCellFromClipboard = (params) => params.value;
    }

    return options;
  }, [gridType, displayType, getMainMenuItems]);

  return (
    <FullScreenTableWrapper hideButton={true} onToggleRef={fullscreenToggleRef}>
      <AgGridReact
        ref={ref}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        gridOptions={gridOptions}
        onGridReady={handleGridReady}
        rowHeight={25}
        headerHeight={25}
        columnMenu="legacy"
        {...restProps}
      />
    </FullScreenTableWrapper>
  );
});

export default CommonAgGrid;
